import {
  ContentContainer,
  EllipsedText,
  FlexRow,
  MaterialIcon,
  StyledSearchBar,
  useDebouncedSearch,
  useLoading,
  useModal,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { JOB_SKILLS_TABLE_COLUMNS_NAME } from '../../constants/jobSkills';
import { useStyles } from '../../hooks/useStyles';
import { getJobSkills } from '../../services/jobsService';
import { Skill } from '../../types/jobs';
import { SkillModal } from '../jobs/skills/SkillModal';
import { TableWrapper } from '../shared/TableWrapper';
import StyledTooltip from '../ui/StyledTooltip';

export function JobSkillsListPage() {
  const { css, theme, classes } = useStyles();
  const [jobSkills, setJobSkills] = useState<Skill[]>();
  const [selectedSkill, setSelectedSkill] = useState<Skill>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState<number | undefined>(undefined);
  const [totalItems, setTotalItems] = useState(0);
  const { search, setSearch } = useDebouncedSearch(useState<string>());
  const { openModal, closeModal, showModal } = useModal();

  useEffect(() => {
    setPage(0);
  }, [search]);

  useEffect(() => {
    if (page === undefined) return;
    if (page === 0) {
      setPage(1);
      return;
    }
    startLoading();
    const loadJobsSkills = async () => {
      const { paginatedResponse, error } = await getJobSkills(page, search);
      if (!error && paginatedResponse) {
        setJobSkills(paginatedResponse.items);
        setTotalItems(paginatedResponse.total);
      } else {
        setJobSkills(undefined);
        setTotalItems(0);
      }
      stopLoading();
    };
    loadJobsSkills();
  }, [page]);

  const onUpdate = () => {
    setPage(0);
  };

  const onOpenModal = (skill?: Skill) => {
    setSelectedSkill(skill);
    openModal();
  };

  const onSubmitFinished = () => {
    onUpdate();
    onCloseModal();
  };

  const onCloseModal = () => {
    setSelectedSkill(undefined);
    closeModal();
  };

  return (
    <ContentContainer>
      <FlexRow gap={theme.sizing.scale300}>
        <div className={classes.flexible(1)}>
          <StyledSearchBar
            name="skills-search"
            value={search}
            onChange={setSearch}
            clearable
            noMargin
          />
        </div>
        <FlexRow
          gap={0}
          classNames={css({ alignItems: 'center' })}
        >
          <StyledTooltip
            content={'Nueva'}
            showArrow={false}
          >
            <Button
              size="mini"
              shape="square"
              onClick={() => onOpenModal()}
              kind="tertiary"
            >
              <MaterialIcon name={'add'} />
            </Button>
          </StyledTooltip>
          <StyledTooltip
            content={'Actualizar'}
            showArrow={false}
          >
            <Button
              size="mini"
              shape="square"
              onClick={onUpdate}
              kind="tertiary"
            >
              <MaterialIcon name={'sync'} />
            </Button>
          </StyledTooltip>
        </FlexRow>
      </FlexRow>
      <TableWrapper
        columns={JOB_SKILLS_TABLE_COLUMNS_NAME}
        data={jobSkills?.map((skill, i) => {
          return {
            items: [
              <EllipsedText key={`skill-name-${i}`}>{skill.name}</EllipsedText>,
              <EllipsedText key={`skill-status-${i}`}>{skill.active ? 'Si' : 'No'}</EllipsedText>,
              <FlexRow key={`jobs-actions-${i}`}>
                <StyledTooltip
                  content={'Nueva'}
                  showArrow={false}
                >
                  <Button
                    size="mini"
                    shape="square"
                    onClick={() => onOpenModal(skill)}
                    kind="tertiary"
                  >
                    <MaterialIcon
                      name={'edit'}
                      size="xs-mini"
                    />
                  </Button>
                </StyledTooltip>
              </FlexRow>,
            ],
          };
        })}
        hasData={jobSkills !== undefined}
        isEmpty={!jobSkills?.length}
        loading={loading}
        noDataDescription="Aun no tienes ninguna habilidad"
        onPageChange={setPage}
        page={page ?? 0}
        totalItems={totalItems}
        hasSearch
      />
      <SkillModal
        isOpen={showModal}
        onClose={onCloseModal}
        onSubmitFinished={onSubmitFinished}
        skill={selectedSkill}
        skillSlug={selectedSkill?.slug}
      />
    </ContentContainer>
  );
}
